// @flow
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { bindAll, get, noop } from "lodash";
import type { NullaryFn } from "@tvg/types/Functional";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import isMobile from "@tvg/utils/mobileUtils";
import { mapSelectionsToString } from "@tvg/utils/talentPicksUtils";
import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import type { TitleType } from "@tvg/atomic-ui/_templates/ModalV2/types";
import BetConfirmation from "@tvg/atomic-ui/_templates/BetConfirmation";
import RaceAlerts from "@tvg/race-alerts";
import type {
  RaceProgram,
  RaceWagerType,
  BettingInterest
} from "@tvg/types/Race";
import {
  setBetConfirmation,
  setPickAction,
  setPickEditButtonAction,
  setShowRepeatBet,
  setBetTicketState,
  type Actions as BetActions,
  setBetRepeatNumber,
  clearBetRepeatNumber
} from "@tvg/shared-actions/BetActions";
import { type Actions as ProgramActions } from "@tvg/shared-actions/ProgramActions";
import { getShowMyBetsCardDesign } from "@tvg/sh-lib-my-bets/redux/selectors";
import RepeatBets from "@tvg/atomic-ui/_templates/RepeatBets";
import { getBetSelectionsToShow } from "@tvg/bet/utils";
import { parseCAPIMessage, replaceCAPIVariables } from "@tvg/utils/capiUtils";
import formatCurrency from "@tvg/formatter/currency";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import { fontCondensedNormal } from "@tvg/atomic-ui/_static/Typography";
import { formatWager, getDateDiff } from "../utils";

import type { Props, State } from "./types";

export const getTitleType = (
  isBetPlaced: boolean,
  isBettingError: boolean
): TitleType => {
  if (isBetPlaced && !isBettingError) {
    return "success";
  }
  if (isBettingError) {
    return "error";
  }

  return "default";
};

export class BetConfirmationModalComponent extends PureComponent<Props, State> {
  static defaultProps = {
    isOpen: false,
    isBetPlaced: false,
    isProcessing: false,
    isBettingError: false,
    errorBetMessage: "",
    onConfirmation: noop,
    onRetain: noop,
    onBackToRace: noop,
    isNotificationsEnabled: false,
    modalType: "bottom",
    modalTopChildren: noop,
    contextBT: "mep",
    favoriteRunners: [],
    bettingInterests: [],
    talentPick: null,
    talentPickTriggerOrigin: null,
    enableRepeatBetsPgmPage: false,
    enableRepeatBetsTalentPicks: false,
    pickEditButton: false,
    repetitionsNr: 1,
    probableValue: "",
    showMyBetsCardDesign: false,
    showBetConfirmation: false,
    betConfirmationTitle: "",
    repeatBetTitle: "",
    showRepeatButton: true,
    hasFeedback: false,
    feedbackType: "error",
    partialFailure: false,
    partialInfo: "",
    partialInfoDetails: "",
    feedbackTitle: "",
    feedbackMsg: "",
    showRepeatBet: false,
    repeatBetsLimit: 25
  };

  tvg: { product: string };

  constructor(props: Props): void {
    super(props);
    bindAll(this, [
      "onClose",
      "onConfirmationHandler",
      "openRepeatBets",
      "renderBetConfirmation",
      "renderRepeatBet",
      "onRepeatBets",
      "closeRepeatBets",
      "onRepeatBetChange",
      "onSetToMax"
    ]);

    this.tvg = tvgConf();

    this.state = {
      repeatBetCounter: 0,
      showBalanceError: false
    };
  }

  onRepeatBets(betsNumber: number) {
    this.props.dispatch(setBetRepeatNumber(betsNumber));
    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBetPlaced: false,
        isBettingError: false,
        errorBetMessage: "",
        betConfirmationTitle: "confirm bet",
        randomTalentPhrase: "",
        randomTalentPhraseId: -1
      })
    );

    mediator.base.dispatch({
      type: "REPEAT_BETS:SET_BETS",
      payload: {
        repeatBet: betsNumber.toString()
      }
    });

    this.closeRepeatBets();
  }

  openRepeatBets() {
    this.props.dispatch(setShowRepeatBet(true));
    if (this.props.showMyBetsCardDesign) {
      this.props.dispatch(setBetConfirmation(false));
      mediator.base.dispatch({
        type: "REPEAT_BETS:SHOW"
      });
    } else {
      mediator.base.dispatch({
        type: "REPEAT_BETS:LINK_CLICK",
        payload: {
          module: this.props.isBetPlaced
            ? "Bet Success Modal"
            : "Confirm Bet Modal",
          tag: this.props.talentPickTriggerOrigin ? "Talent" : undefined
        }
      });
    }
  }

  closeRepeatBets() {
    this.props.dispatch(setShowRepeatBet(false));
    this.props.dispatch(setBetConfirmation(true));
  }

  onClose() {
    if (this.props.isBetPlaced && !this.props.isBettingError) {
      // the bet was placed - clean betting and remove bet ticket
      this.props.onBackToRace();

      mediator.base.dispatch({
        type: "BET_CONFIRMATION:CLOSE_CONFIRM_MODAL",
        payload: {
          confirmMode: false
        }
      });
    } else {
      // when bet is not placed hide betConfirmation
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
      setTimeout(() => this.props.resetState(), 400);

      if (this.props.talentPick && this.props.contextBT === "mep") {
        mediator.base.dispatch({
          type: "TP_PROGRAMPAGE_CONFIRM_BET_CLOSE",
          payload: {
            action: "Close",
            label: "TP Confirm Bet Modal"
          }
        });
      } else {
        mediator.base.dispatch({
          type: "BET_CONFIRMATION:CLOSE_CONFIRM_MODAL",
          payload: {
            confirmMode: true
          }
        });
      }
    }

    if (this.props.talentPick) {
      this.props.dispatch(setPickAction(null));
      this.props.dispatch(setPickEditButtonAction(true));
    }

    if (this.props.showMyBetsCardDesign) {
      this.props.dispatch(setShowRepeatBet(false));
      this.props.dispatch(clearBetRepeatNumber());
      this.setState({
        repeatBetCounter: 0
      });
    }
  }

  onConfirmationHandler() {
    if (this.props.talentPick && this.props.contextBT === "mep") {
      mediator.base.dispatch({
        type: "TP_PROGRAMPAGE_CONFIRM_BET_OK",
        payload: {
          action: "Bet Confirm",
          label: `Talent Pick - ${get(this.props.talentPick, "talent.name")}`,
          talentPickOrigin: this.props.talentPickTriggerOrigin,
          tag: mapSelectionsToString(
            get(this.props.talentPick, "selections", [])
          ),
          race: {
            raceNumber: get(this.props.talentPick, "race"),
            track: {
              trackName: get(this.props.talentPick, "trackName")
            },
            type: {
              code: get(this.props.talentPick, "raceType")
            }
          },
          betAmount: get(this.props.talentPick, "amount"),
          betType: get(this.props.talentPick, "wagerType.abbreviation")
        }
      });
    } else {
      mediator.base.dispatch({
        type: "BET_CONFIRMATION:CONFIRM_BET",
        payload: {
          selections: get(this.props, "betSelections").join(", "),
          trackName: get(this.props, "race.track.trackName"),
          raceNumber: get(this.props, "race.raceNumber"),
          betAmount: get(this.props, "betAmount"),
          betType: get(this.props, "betType.type.code"),
          runnerType:
            get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
        }
      });
    }

    if (this.props.showMyBetsCardDesign) {
      this.setState({
        repeatBetCounter: 0,
        showBalanceError: false
      });
    }

    this.props.onConfirmation(this.props.betsNumber);
  }

  renderBetConfirmation(betTotal: number) {
    const hasRaceAlertsBar =
      isMobile(this.tvg.product) && this.props.isBetPlaced;

    const bettingInterests = get(this.props, "bettingInterests", [[]]);
    const selections = getBetSelectionsToShow(
      this.props.betSelections,
      bettingInterests
    );
    const wagerTypeName = get(
      this.props,
      "betType.type.name",
      ""
    ).toUpperCase();
    const racePostTime = get(this.props, "race.postTime", "");
    const mtp = get(this.props, "race.mtp");
    const raceNumber = get(this.props, "race.raceNumber");
    const trackName = get(this.props, "race.track.trackName");
    const trackCode = get(this.props, "race.track.trackCode");
    const wagerAmount = get(this.props, "betAmount");
    const raceTypeAbbreviation = get(this.props, "race.type.code");
    const probable = get(this.props, "probableValue");

    return (
      <BetConfirmation
        wager={formatWager({
          betTotal,
          cancelable: false,
          racePostTime,
          mtp,
          raceNumber,
          raceTypeAbbreviation,
          selections,
          bettingInterests,
          trackName,
          trackCode,
          wagerAmount,
          winningsAmount: betTotal,
          probable,
          wagerType: get(this.props, "betType.type", {})
        })}
        postTime={racePostTime}
        isProcessing={this.props.isProcessing}
        mtp={mtp}
        raceNumber={raceNumber}
        trackName={trackName}
        isGreyhound={raceTypeAbbreviation === "G"}
        raceTypeCode={raceTypeAbbreviation}
        wagerTypeName={wagerTypeName}
        wagerCode={get(this.props, "betType.type.code")}
        isKey={get(this.props, "betType.isKey", false)}
        isBox={get(this.props, "betType.isBox", false)}
        isLeg={get(this.props, "betType.legCount", 1) > 1}
        betTotal={betTotal * this.props.repetitionsNr}
        wagerAmount={wagerAmount}
        isCancelable={false}
        selections={selections}
        bettingInterests={bettingInterests}
        favoriteRunners={this.props.favoriteRunners}
        onConfirmation={this.onConfirmationHandler}
        onRetain={this.props.onRetain}
        onBackToRace={this.props.onBackToRace}
        isBetPlaced={this.props.isBetPlaced}
        isBettingError={this.props.isBettingError}
        errorBetMessage={this.props.errorBetMessage}
        hasRaceAlertsBar={hasRaceAlertsBar}
        confirmText={
          this.props.contextBT === "mep"
            ? "Confirm $amount Bet"
            : "Publish Pick"
        }
        onRepeatClick={this.openRepeatBets}
        hasRepeaterEnabled={
          (!this.props.pickEditButton &&
            this.props.isBetPlaced &&
            this.props.enableRepeatBetsTalentPicks) ||
          (this.props.pickEditButton && this.props.enableRepeatBetsPgmPage)
        }
        repetitionsNr={this.props.repetitionsNr}
        probableValue={probable}
        showMyBetsCardDesign={this.props.showMyBetsCardDesign}
        dateDiff={getDateDiff(racePostTime)}
        showRepeatButton={this.props.showRepeatButton}
        hasFeedback={this.props.hasFeedback}
        feedbackType={this.props.feedbackType}
        partialFailure={this.props.partialFailure}
        partialInfo={this.props.partialInfo}
        partialInfoDetails={this.props.partialInfoDetails}
        feedbackTitle={this.props.feedbackTitle}
        feedbackMsg={this.props.feedbackMsg}
        renderRaceAlerts={() => (
          <RaceAlerts
            raceTrackCode={get(this.props, "race.track.trackCode", "")}
            raceNumber={get(this.props, "race.raceNumber", "")}
            racePostTime={racePostTime}
            module="Bet Success Modal"
            tvgRaceId={get(this.props, "race.tvgRaceId")}
          />
        )}
      />
    );
  }

  onRepeatBetChange(repeatBetCounter: number) {
    this.setState({
      repeatBetCounter
    });
  }

  onSetToMax(maxBet: number) {
    mediator.base.dispatch({
      type: "REPEAT_BETS:SET_TO_MAX"
    });

    this.setState({
      repeatBetCounter: maxBet
    });
  }

  renderRepeatBet(betTotal: number) {
    const maxCounter = this.props.repeatBetsLimit;
    const maxByAmount = Math.floor(this.props.balance / betTotal);
    const isMaxCounterExceeded = this.state.repeatBetCounter > maxCounter;
    const isBalanceExceeded = this.state.repeatBetCounter > maxByAmount;
    const showBalanceError = maxByAmount < maxCounter;
    const notification = showBalanceError
      ? {
          message: get(this.props, "notificationText.balanceExceeded", ""),
          counter: maxByAmount
        }
      : {
          message: get(this.props, "notificationText.maxExceeded", ""),
          counter: maxCounter
        };

    this.setState({
      showBalanceError:
        showBalanceError && this.state.repeatBetCounter > maxByAmount
    });

    return (
      <RepeatBets
        counter={this.state.repeatBetCounter}
        onChangeValue={this.onRepeatBetChange}
        betAmount={betTotal}
        maxCounter={maxCounter}
        maxCounterByAmount={maxByAmount}
        onRepeatBets={this.onRepeatBets}
        onSetToMax={this.onSetToMax}
        isMaxCounterExceeded={isMaxCounterExceeded}
        isBalanceExceeded={isBalanceExceeded}
        notificationText={replaceCAPIVariables(notification.message, {
          counter: notification.counter
        })}
      />
    );
  }

  render() {
    const baseProps = {
      title: this.props.betConfirmationTitle,
      titleType: getTitleType(
        this.props.isBetPlaced,
        this.props.isBettingError
      ),
      animation: this.props.modalType,
      isOpen: this.props.isOpen,
      onClose: this.onClose,
      isFullHeight: false,
      qaLabel: "modal-betConfirmation",
      key: "betConfirmationModal",
      modalTopChildren: this.props.modalTopChildren
    };

    const basePropsRepeat =
      !this.props.showBetConfirmation && this.props.showMyBetsCardDesign
        ? {
            title: this.props.repeatBetTitle,
            titleType: "default",
            subtitle: `BALANCE ${formatCurrency(this.props.balance)}`,
            isTitleCenter: true,
            useModalHeaderV3: true,
            onBack: () => {
              mediator.base.dispatch({
                type: "REPEAT_BETS:BACK"
              });
              this.setState({
                repeatBetCounter:
                  this.props.betsNumber > 1 ? this.props.betsNumber : 0
              });
              this.closeRepeatBets();
            },
            subtitleColor: this.state.showBalanceError
              ? buildColor("orange", "700")
              : buildColor("grey", "800"),
            subtitleFontFamily: fontCondensedNormal
          }
        : {};

    const props =
      this.props.modalType === "bottom"
        ? {
            ...baseProps,
            ...basePropsRepeat,
            hasVideoOffset: this.props.isVideoShown
          }
        : {
            ...baseProps,
            ...basePropsRepeat,
            isFullWidth: false,
            offsetTop: 0
          };

    const totals =
      this.props.isOpen &&
      get(this.props, "wagerType.columnCount") &&
      calculateTotals(
        get(this.props, "betAmount"),
        get(this.props, "betType"),
        get(this.props, "betSelections")
      );
    const betTotal = this.props.betAmount * get(totals, "betCount", 0);

    return (
      <ModalV2 {...props}>
        {() => (
          <div>
            {this.props.showRepeatBet && this.props.showMyBetsCardDesign
              ? this.renderRepeatBet(betTotal)
              : this.renderBetConfirmation(betTotal)}
          </div>
        )}
      </ModalV2>
    );
  }
}

export default connect(
  (
    store,
    props: {
      race: RaceProgram,
      wagerType: RaceWagerType,
      betSelections: string[][],
      betAmount: number,
      showBetConfirmation: boolean,
      isVideoShown: boolean,
      modalTopChildren: NullaryFn<mixed>,
      bettingInterests: Array<Array<BettingInterest>>,
      showMyBetsCardDesign: boolean,
      showRepeatBet: boolean,
      repeatBetsLimit: number
    }
  ) => ({
    race: props.race,
    bettingInterests: props.bettingInterests,
    betType: props.wagerType,
    betAmount: props.betAmount,
    showBetConfirmation: props.showBetConfirmation,
    modalTopChildren: props.modalTopChildren,
    betSelections: props.betSelections,
    isVideoShown: props.isVideoShown,
    showRepeatBet: props.showRepeatBet,
    enableRepeatBetsPgmPage: get(
      store,
      "capi.featureToggles.enableRepeatBetsPgmPage"
    ),
    enableRepeatBetsTalentPicks: get(
      store,
      "capi.featureToggles.enableRepeatBetsTalentPicks"
    ),
    talentPick: get(store, "RaceProgram.talentPick", null),
    talentPickTriggerOrigin: get(
      store,
      "RaceProgram.talentPickTriggerOrigin",
      null
    ),
    pickEditButton: get(store, "RaceProgram.pickEditButton", null),
    showMyBetsCardDesign: getShowMyBetsCardDesign(store),
    balance: get(store, "userData.balance"),
    notificationText: parseCAPIMessage(store, "capi.messages.betConfirmation"),
    betsNumber: get(store, "BetTicket.repeatBetNumber"),
    repeatBetsLimit: +get(store, "capi.messages.repeatBetsLimit", 25)
  }),
  (dispatch: Dispatch<BetActions | ProgramActions>) => ({ dispatch })
)(BetConfirmationModalComponent);
